import { t } from "../i18n.js"
const NAVIGATION_FRAME_SELECTOR = '[data-testid="workspace-resource-frame"]'
const ROOM_EDITOR_FRAME_SELECTOR = [
  '[data-testid="committee-builder-frame"]',
  '[data-testid="workspace-agent-resource-frame"]',
  '[data-testid="committee-agent-app-test-frame"]',
].join(",")
const MONICA_FRAME_SELECTOR = [
  NAVIGATION_FRAME_SELECTOR,
  ROOM_EDITOR_FRAME_SELECTOR,
].join(",")
const HITL_DRAWER_SELECTOR =
  '#workspace-hitl-drawer, [data-testid="committee-hitl-panel"]'
const HITL_PRESENT_CLASS = "committee-hitl-present"
const HITL_EXPANDED_CLASS = "committee-hitl-expanded"
const INLINE_MONICA_PANEL_SELECTOR = '[data-testid="committee-monica-panel"]'
const INLINE_MONICA_PRESENT_CLASS = "committee-inline-monica-present"
const INLINE_MONICA_EXPANDED_CLASS = "committee-inline-monica-expanded"
const PLAN_PANEL_SELECTOR = '[data-testid="committee-plan-panel"]'
const PLAN_PRESENT_CLASS = "committee-plan-present"
const PLAN_EXPANDED_CLASS = "committee-plan-expanded"
const LAYOUT_CHANGED_EVENT = "seiva:committee-layout-changed"
const MONICA_TOOLTIP_PORTAL_SELECTOR =
  '[data-committee-editor-monica-tooltip-portal="true"]'
const MONICA_TOOLTIP_GAP = 8
const MONICA_TOOLTIP_VIEWPORT_PADDING = 8

function monicaTooltipGeometry(data) {
  const rect = data?.rect
  const viewport = data?.viewport
  const rectKeys = ["top", "right", "bottom", "left", "width", "height"]

  if (
    !rect ||
    !viewport ||
    !rectKeys.every(key => Number.isFinite(rect[key])) ||
    !Number.isFinite(viewport.width) ||
    !Number.isFinite(viewport.height) ||
    viewport.width <= 0 ||
    viewport.height <= 0
  ) {
    return null
  }

  return {rect, viewport}
}

export function monicaTooltipPosition(request, frameRect, tooltipRect, viewport) {
  const scaleX = frameRect.width / request.viewport.width
  const scaleY = frameRect.height / request.viewport.height
  const anchorRight = frameRect.left + request.rect.right * scaleX
  const anchorTop = frameRect.top + request.rect.top * scaleY
  const anchorBottom = frameRect.top + request.rect.bottom * scaleY
  const padding = MONICA_TOOLTIP_VIEWPORT_PADDING
  const above = anchorTop - tooltipRect.height - MONICA_TOOLTIP_GAP
  const preferredTop = above >= padding ? above : anchorBottom + MONICA_TOOLTIP_GAP
  return {
    left: Math.min(Math.max(anchorRight - tooltipRect.width, padding), Math.max(viewport.width - tooltipRect.width - padding, padding)),
    top: Math.min(Math.max(preferredTop, padding), Math.max(viewport.height - tooltipRect.height - padding, padding)),
  }
}

function syncHitlLayout(shell) {
  const hitlDrawer = shell.querySelector(HITL_DRAWER_SELECTOR)
  const hitlPresent = Boolean(hitlDrawer)
  const hitlExpanded = hitlDrawer?.dataset.collapsed === "false"
  const inlineMonicaPanel = shell.querySelector(INLINE_MONICA_PANEL_SELECTOR)
  const inlineMonicaPresent =
    Boolean(inlineMonicaPanel) && inlineMonicaPanel?.dataset.layoutVisible !== "false"
  const inlineMonicaExpanded = inlineMonicaPanel?.dataset.collapsed === "false"
  const planPanel = shell.querySelector(PLAN_PANEL_SELECTOR)
  const planPresent = Boolean(planPanel)
  const planExpanded = planPanel?.dataset.collapsed === "false"
  const root = document.documentElement
  const layoutChanged =
    root.classList.contains(HITL_PRESENT_CLASS) !== hitlPresent ||
    root.classList.contains(HITL_EXPANDED_CLASS) !== hitlExpanded ||
    root.classList.contains(INLINE_MONICA_PRESENT_CLASS) !== inlineMonicaPresent ||
    root.classList.contains(INLINE_MONICA_EXPANDED_CLASS) !== inlineMonicaExpanded ||
    root.classList.contains(PLAN_PRESENT_CLASS) !== planPresent ||
    root.classList.contains(PLAN_EXPANDED_CLASS) !== planExpanded

  root.classList.toggle(HITL_PRESENT_CLASS, hitlPresent)
  root.classList.toggle(HITL_EXPANDED_CLASS, hitlExpanded)
  root.classList.toggle(INLINE_MONICA_PRESENT_CLASS, inlineMonicaPresent)
  root.classList.toggle(INLINE_MONICA_EXPANDED_CLASS, inlineMonicaExpanded)
  root.classList.toggle(PLAN_PRESENT_CLASS, planPresent)
  root.classList.toggle(PLAN_EXPANDED_CLASS, planExpanded)

  if (layoutChanged) window.dispatchEvent(new CustomEvent(LAYOUT_CHANGED_EVENT))
}

function clearHitlLayout() {
  const root = document.documentElement
  const layoutChanged =
    root.classList.contains(HITL_PRESENT_CLASS) ||
    root.classList.contains(HITL_EXPANDED_CLASS) ||
    root.classList.contains(INLINE_MONICA_PRESENT_CLASS) ||
    root.classList.contains(INLINE_MONICA_EXPANDED_CLASS) ||
    root.classList.contains(PLAN_PRESENT_CLASS) ||
    root.classList.contains(PLAN_EXPANDED_CLASS)

  root.classList.remove(
    HITL_PRESENT_CLASS,
    HITL_EXPANDED_CLASS,
    INLINE_MONICA_PRESENT_CLASS,
    INLINE_MONICA_EXPANDED_CLASS,
    PLAN_PRESENT_CLASS,
    PLAN_EXPANDED_CLASS
  )

  if (layoutChanged) window.dispatchEvent(new CustomEvent(LAYOUT_CHANGED_EVENT))
}

// Keeps the canonical `/committee/...` URL in the parent Cockpit synchronized
// with navigation performed by a same-origin resource/editor frame. It also
// publishes the Committee's right-side panel geometry on <html>, because the
// global chat launchers cannot inspect those LiveView panels directly.
const CommitteeShellNavigation = {
  mounted() {
    document
      .querySelectorAll(MONICA_TOOLTIP_PORTAL_SELECTOR)
      .forEach(tooltip => tooltip.remove())

    this.monicaTooltip = document.createElement("span")
    this.monicaTooltip.id = "committee-editor-monica-tooltip-portal"
    this.monicaTooltip.className = "seiva-editor-monica-tooltip-portal"
    this.monicaTooltip.dataset.committeeEditorMonicaTooltipPortal = "true"
    this.monicaTooltip.dataset.testid = "committee-editor-monica-tooltip-portal"
    this.monicaTooltip.dataset.open = "false"
    this.monicaTooltip.setAttribute("popover", "manual")
    this.monicaTooltip.setAttribute("role", "tooltip")
    this.monicaTooltip.setAttribute("aria-hidden", "true")
    document.body.appendChild(this.monicaTooltip)

    this.onMonicaTooltipFrameLoad = () => this.hideMonicaTooltip?.()

    this.hideMonicaTooltip = () => {
      this.monicaTooltipRequest = null
      this.monicaTooltipFrameObserver?.disconnect()
      this.monicaTooltipSourceFrame?.removeEventListener(
        "load",
        this.onMonicaTooltipFrameLoad,
      )
      this.monicaTooltipSourceFrame = null

      if (!this.monicaTooltip) return

      this.monicaTooltip.dataset.open = "false"

      try {
        this.monicaTooltip.hidePopover?.()
      } catch (_error) {
        // Browsers without the Popover API use the max-z-index CSS fallback.
      }
    }

    this.positionMonicaTooltip = () => {
      const request = this.monicaTooltipRequest

      if (
        !request ||
        !this.monicaTooltip ||
        !request.sourceFrame.isConnected ||
        !this.el.contains(request.sourceFrame)
      ) {
        if (request) this.hideMonicaTooltip()
        return
      }

      const frameRect = request.sourceFrame.getBoundingClientRect()
      const tooltipRect = this.monicaTooltip.getBoundingClientRect()
      const {left, top} = monicaTooltipPosition(request, frameRect, tooltipRect, {
        width: window.innerWidth,
        height: window.innerHeight,
      })

      this.monicaTooltip.style.left = `${Math.round(left)}px`
      this.monicaTooltip.style.top = `${Math.round(top)}px`
    }

    this.showMonicaTooltip = (sourceFrame, data) => {
      const geometry = monicaTooltipGeometry(data)
      if (!geometry || !this.monicaTooltip) return

      const text = typeof data.text === "string" ? data.text.trim().slice(0, 160) : ""
      this.monicaTooltip.textContent = text || t("Edit with Monica")
      this.monicaTooltipRequest = {sourceFrame, ...geometry}
      this.monicaTooltip.dataset.open = "true"

      try {
        this.monicaTooltip.showPopover?.()
      } catch (_error) {
        // Calling showPopover twice throws in some engines. It is already in
        // the top layer in that case, so only its position needs refreshing.
      }

      this.monicaTooltipFrameObserver?.disconnect()
      this.monicaTooltipFrameObserver?.observe(sourceFrame)

      if (this.monicaTooltipSourceFrame !== sourceFrame) {
        this.monicaTooltipSourceFrame?.removeEventListener(
          "load",
          this.onMonicaTooltipFrameLoad,
        )
        this.monicaTooltipSourceFrame = sourceFrame
        sourceFrame.addEventListener("load", this.onMonicaTooltipFrameLoad)
      }

      this.positionMonicaTooltip()
    }

    this.monicaTooltipFrameObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(() => this.positionMonicaTooltip())
        : null
    this.onCommitteeLayoutChanged = () => this.positionMonicaTooltip()

    this.onCommitteeMessage = event => {
      if (event.origin !== window.location.origin) return

      const sourceFrame = Array.from(this.el.querySelectorAll(MONICA_FRAME_SELECTOR)).find(
        frame => event.source === frame.contentWindow
      )
      if (!sourceFrame) return

      if (
        event.data?.type === "seiva:committee:navigate" &&
        sourceFrame.matches(NAVIGATION_FRAME_SELECTOR)
      ) {
        this.pushEvent("committee_frame_navigate", {path: event.data.path})
      } else if (
        event.data?.type === "seiva:committee:navigate" &&
        sourceFrame.matches(ROOM_EDITOR_FRAME_SELECTOR)
      ) {
        this.pushEvent("committee_editor_frame_navigate", {path: event.data.path})
      } else if (
        event.data?.type === "seiva:committee:home" &&
        sourceFrame.matches(NAVIGATION_FRAME_SELECTOR) &&
        sourceFrame.dataset.frameKind === "home"
      ) {
        // The hosted Cockpit Overview (Home App), relayed by the frame's bridge
        // in app.js. Only the action and small scalar fields cross; the server
        // revalidates each of them (`committee_home_event`).
        const action = event.data.action
        if (["ready", "unavailable", "navigate", "settings", "hitl"].includes(action)) {
          const payload = {action}
          if (typeof event.data.path === "string") payload.path = event.data.path.slice(0, 2000)
          if (typeof event.data.approvalId === "string")
            payload.approval_id = event.data.approvalId.slice(0, 64)
          this.pushEvent("committee_home_event", payload)
        }
      } else if (event.data?.type === "seiva:committee:monica-tooltip") {
        if (event.data.action === "show") {
          this.showMonicaTooltip(sourceFrame, event.data)
        } else if (
          event.data.action === "hide" &&
          (!this.monicaTooltipRequest ||
            this.monicaTooltipRequest.sourceFrame === sourceFrame)
        ) {
          this.hideMonicaTooltip()
        }
      } else if (event.data?.type === "seiva:committee:editor-monica") {
        this.pushEvent("committee_editor_monica_state", {
          action: event.data.action === "present" ? "present" : "gone",
          builder_mode:
            typeof event.data.builderMode === "string" ? event.data.builderMode : null,
          resource_id:
            typeof event.data.resourceId === "string" ? event.data.resourceId : null,
        })
      } else if (event.data?.type === "seiva:committee:build-fix") {
        this.pushEvent("fix_error_from_editor", {
          app_id: event.data.app_id,
          kind: event.data.kind === "build" ? "build" : "runtime",
          message: String(event.data.message || "").slice(0, 800),
          stack: String(event.data.stack || "").slice(0, 4000),
          raw: String(event.data.raw || "").slice(0, 4000),
        })
      } else if (
        event.data?.type === "seiva:committee:app-agent" &&
        sourceFrame.matches(NAVIGATION_FRAME_SELECTOR)
      ) {
        // The host already owns the authorized viewing_app_id. Never accept
        // an app/agent identity from the frame as authority for the drawer.
        this.pushEvent("aios_toggle_assistant", {})
      } else if (event.data?.type === "seiva:committee:open-monica") {
        const builderMode =
          typeof event.data.builderMode === "string" ? event.data.builderMode : null
        const resourceId =
          typeof event.data.resourceId === "string" ? event.data.resourceId : null

        this.hideMonicaTooltip()
        this.pushEvent("open_editor_monica", {
          builder_mode: builderMode,
          resource_id: resourceId,
        })
      }
    }

    this.hitlLayoutObserver = new MutationObserver(() => {
      syncHitlLayout(this.el)
      this.positionMonicaTooltip()
    })
    this.hitlLayoutObserver.observe(this.el, {
      attributes: true,
      attributeFilter: ["data-collapsed", "data-layout-visible"],
      childList: true,
      subtree: true,
    })

    syncHitlLayout(this.el)
    window.addEventListener("message", this.onCommitteeMessage)
    window.addEventListener("resize", this.positionMonicaTooltip)
    window.addEventListener("scroll", this.positionMonicaTooltip, true)
    window.addEventListener(LAYOUT_CHANGED_EVENT, this.onCommitteeLayoutChanged)
  },

  updated() {
    syncHitlLayout(this.el)
    this.positionMonicaTooltip()
  },

  destroyed() {
    this.hitlLayoutObserver?.disconnect()
    this.monicaTooltipFrameObserver?.disconnect()
    window.removeEventListener("message", this.onCommitteeMessage)
    window.removeEventListener("resize", this.positionMonicaTooltip)
    window.removeEventListener("scroll", this.positionMonicaTooltip, true)
    window.removeEventListener(LAYOUT_CHANGED_EVENT, this.onCommitteeLayoutChanged)
    this.hideMonicaTooltip?.()
    this.monicaTooltip?.remove()
    clearHitlLayout()
  },
}

export default CommitteeShellNavigation
